import type { RecipeLang } from './recipeSeo';

/**
 * Minimalny słownik UI dla publicznych stron przepisów (pl/en).
 * Używany przez PublicRecipeDetailPage i komponenty PublicRecipes —
 * prop `lang` (default 'pl') zachowuje dotychczasowe zachowanie.
 */
export const recipeUi = (lang: RecipeLang) => {
  const en = lang === 'en';
  return {
    backToCatalog: en ? 'All recipes' : 'Wszystkie przepisy',
    backToCatalogAria: en ? 'Back to recipe catalog' : 'Wróć do katalogu przepisów',
    loading: en ? 'Loading…' : 'Ładowanie…',
    notFoundTitle: en ? 'Recipe not found' : 'Nie znaleziono przepisu',
    notFoundText: en
      ? 'This recipe does not exist or is not public.'
      : 'Ten przepis nie istnieje albo nie jest publiczny.',
    backToList: en ? 'Back to list' : 'Wróć do listy',
    loadError: en
      ? 'Loading error. Check your internet connection and try again.'
      : 'Błąd ładowania. Sprawdź połączenie z internetem i spróbuj ponownie.',
    similarTitle: en ? 'Similar recipes' : 'Podobne przepisy',
    viewAll: en ? 'See all' : 'Zobacz wszystkie',
    ingredients: en ? 'Ingredients' : 'Składniki',
    instructions: en ? 'Preparation' : 'Sposób przygotowania',
    servings: en ? 'Servings' : 'Porcje',
    servingsBase: en ? 'Base' : 'Bazowo',
    servingsAria: en ? 'Servings count' : 'Liczba porcji',
    servingsDecrease: en ? 'Decrease servings' : 'Zmniejsz porcje',
    servingsIncrease: en ? 'Increase servings' : 'Zwiększ porcje',
    metric: en ? 'Metric' : 'Metryczne',
    kitchen: en ? 'Kitchen' : 'Kuchenne',
    measureAria: en ? 'Measurement units' : 'Jednostki miary',
    per100: en ? 'Per 100 g / product serving' : 'Na 100 g / porcję produktu',
    forThisAmount: en ? 'For this amount' : 'Dla tej ilości',
    check: en ? 'Check' : 'Odhacz',
    uncheck: en ? 'Uncheck' : 'Odznacz',
    copy: en ? 'Copy ingredients' : 'Kopiuj składniki',
    copyShort: en ? 'Copy' : 'Kopiuj',
    copied: en ? 'Copied!' : 'Skopiowano!',
    copyFailed: en ? 'Copy failed' : 'Nie udało się skopiować',
    copyFailedShort: en ? 'Error' : 'Błąd',
    perServing: en ? 'Per serving' : 'Na porcję',
    wholeRecipe: en ? 'Whole recipe' : 'Cały przepis',
    energyShare: en ? 'Energy share from macros' : 'Udział energii z makroskładników',
    addFavorite: en ? 'Add to favorites' : 'Dodaj do ulubionych',
    inFavorites: en ? 'In favorites' : 'W ulubionych',
    macroProtein: en ? 'Protein' : 'Białko',
    macroCarbs: en ? 'Carbs' : 'Węglowodany',
    macroFat: en ? 'Fat' : 'Tłuszcz',
    macroCalories: en ? 'Calories' : 'Kalorie',
    nutritionPerServing: en ? 'Nutrition per serving' : 'Wartości odżywcze na porcję',
    nutritionTotal: en ? 'Nutrition for the whole recipe' : 'Wartości odżywcze całego przepisu',
    source: en ? 'Source' : 'Źródło',
    sourceWebsite: en ? 'Website' : 'Strona WWW',
    totalTime: en ? 'total' : 'łącznie',
    prepTime: en ? 'Prep' : 'Przygotowanie',
    cookTime: en ? 'Cooking' : 'Gotowanie',
    stepsCount: (n: number) => (en ? `${n} steps` : `${n} etapów`),
    timerPause: en ? 'Pause' : 'Pauza',
    timerResume: en ? 'Resume' : 'Wznów',
    timerStart: (m: number) => (en ? `Start timer (${m} min)` : `Uruchom stoper (${m} min)`),
  };
};

export type RecipeUiStrings = ReturnType<typeof recipeUi>;
